// UserSubscriptionsPage.jsx - Version refondue avec optimisations mobiles
import React, { useEffect, useState, useMemo } from "react";
import { useSubscriptionStore } from "../store/subscriptionStore";
import { toast } from "react-toastify";
import {
  BiCalendar,
  BiReset,
  BiSearch,
  BiPackage,
  BiUser,
  BiListUl,
  BiX,
  BiDollar,
  BiTime,
  BiCheckCircle,
  BiCopy,
  BiCheck,
  BiXCircle,
  BiCreditCard,
  BiChevronLeft,
  BiChevronRight,
  BiChevronsLeft,
  BiChevronsRight,
  BiFilter,
  BiCheckSquare,
  BiSquare,
  BiCalendarCheck,
  BiCalendarEvent,
  BiPhone,
  BiEnvelope,
} from "react-icons/bi";
import { motion, AnimatePresence } from "framer-motion";

export default function UserSubscriptionsPage() {
  const {
    userSubscriptions,
    loading,
    error,
    fetchUserSubscriptions,
    fetchSubscriptionById,
  } = useSubscriptionStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  // États pour les filtres de période
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Chargement initial
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      await fetchUserSubscriptions();
    } catch (error) {
      console.error("Erreur lors du chargement initial:", error);
      toast.error("Erreur lors du chargement des abonnements");
    }
  };

  // Fonction pour obtenir les dates d'une période
  const getPeriodDates = (period) => {
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    switch (period) {
      case "today":
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case "week":
        start.setDate(today.getDate() - today.getDay());
        start.setHours(0, 0, 0, 0);
        end.setDate(today.getDate() + (6 - today.getDay()));
        end.setHours(23, 59, 59, 999);
        break;
      case "month":
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        return { startDate: "", endDate: "" };
    }

    return {
      startDate: formatDate(start),
      endDate: formatDate(end),
    };
  };

  // Charger les abonnements avec les filtres
  const loadSubscriptions = async (
    period = periodFilter,
    customStartDate = startDate,
    customEndDate = endDate,
  ) => {
    try {
      let start = customStartDate;
      let end = customEndDate;

      if (period !== "custom" && period !== "all") {
        const dates = getPeriodDates(period);
        start = dates.startDate;
        end = dates.endDate;
        setStartDate(dates.startDate);
        setEndDate(dates.endDate);
      }

      await fetchUserSubscriptions();
    } catch (error) {
      console.error("Erreur lors du chargement des abonnements:", error);
      toast.error("Erreur lors du chargement des abonnements");
    } finally {
      setCurrentPage(1);
    }
  };

  const handlePeriodChange = async (period) => {
    setPeriodFilter(period);

    if (period === "all") {
      setStartDate("");
      setEndDate("");
      await loadSubscriptions("all", "", "");
    } else if (period !== "custom") {
      const dates = getPeriodDates(period);
      await loadSubscriptions(period, dates.startDate, dates.endDate);
    }
  };

  // Effet pour les dates personnalisées
  useEffect(() => {
    if (periodFilter === "custom" && startDate && endDate) {
      const timer = setTimeout(() => {
        loadSubscriptions("custom", startDate, endDate);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [startDate, endDate, periodFilter]);

  // Fonction pour ouvrir le modal des détails
  const handleShowDetails = async (subscription) => {
    setSelectedSubscription(subscription);
    setShowDetailsModal(true);
  };

  // Fonction pour fermer le modal
  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedSubscription(null);
  };

  const resetFilters = async () => {
    setPeriodFilter("all");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
    setStatusFilter("");
    setCurrentPage(1);
    await loadSubscriptions("all", "", "");
  };

  const getPeriodLabel = () => {
    switch (periodFilter) {
      case "today":
        return "Aujourd'hui";
      case "week":
        return "Cette semaine";
      case "month":
        return "Ce mois";
      case "custom":
        return "Période personnalisée";
      default:
        return "Toutes les périodes";
    }
  };

  // Fonction pour formater le montant
  const formatAmount = (amount) => {
    return parseFloat(amount || 0).toLocaleString("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "PENDING":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "EXPIRED":
        return "bg-rose-50 text-rose-700 border-rose-200";
      case "CANCELLED":
        return "bg-gray-50 text-gray-700 border-gray-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  // Fonction pour obtenir le libellé du statut
  const getStatusLabel = (status) => {
    switch (status) {
      case "ACTIVE":
        return "Actif";
      case "PENDING":
        return "En attente";
      case "EXPIRED":
        return "Expiré";
      case "CANCELLED":
        return "Annulé";
      default:
        return status;
    }
  };

  // Fonction pour obtenir l'icône du statut
  const getStatusIcon = (status) => {
    switch (status) {
      case "ACTIVE":
        return <BiCheckCircle className="w-4 h-4" />;
      case "PENDING":
        return <BiTime className="w-4 h-4" />;
      case "EXPIRED":
        return <BiXCircle className="w-4 h-4" />;
      case "CANCELLED":
        return <BiSquare className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Fonction pour obtenir la couleur du service utilisé
  const getServiceUsedColor = (used) => {
    return used
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-blue-50 text-blue-700 border-blue-200";
  };

  // Fonction pour obtenir l'icône du service utilisé
  const getServiceUsedIcon = (used) => {
    return used ? (
      <BiCheckSquare className="w-4 h-4" />
    ) : (
      <BiTime className="w-4 h-4" />
    );
  };

  // Format de date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Format de date avec heure
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fonction pour limiter le texte
  const truncateText = (text, maxLength = 50) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Filtrage des abonnements
  const filteredSubscriptions = useMemo(() => {
    return userSubscriptions.filter((subscription) => {
      const matchesSearch =
        !searchTerm ||
        subscription.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subscription.plan?.name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (subscription.is_anonymous
          ? subscription.anonymous_data?.name
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
          : `${subscription.client.nom} ${subscription.client.prenom}`
              .toLowerCase()
              .includes(searchTerm.toLowerCase()));

      const matchesStatus =
        !statusFilter || subscription.status === statusFilter;

      // Filtre par date si période personnalisée
      let matchesDate = true;
      if (periodFilter === "custom" && startDate && endDate) {
        const subDate = new Date(subscription.created_at)
          .toISOString()
          .split("T")[0];
        matchesDate = subDate >= startDate && subDate <= endDate;
      }

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [
    userSubscriptions,
    searchTerm,
    statusFilter,
    periodFilter,
    startDate,
    endDate,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredSubscriptions.length / itemsPerPage);
  const paginatedSubscriptions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredSubscriptions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredSubscriptions, currentPage, itemsPerPage]);

  // Calcul des statistiques
  const stats = useMemo(() => {
    const totalSubscriptions = userSubscriptions.length;
    const activeSubscriptions = userSubscriptions.filter(
      (s) => s.status === "ACTIVE",
    ).length;
    const pendingSubscriptions = userSubscriptions.filter(
      (s) => s.status === "PENDING",
    ).length;
    const totalAmount = userSubscriptions.reduce(
      (sum, s) => sum + parseFloat(s.amount || 0),
      0,
    );

    return {
      totalSubscriptions,
      activeSubscriptions,
      pendingSubscriptions,
      totalAmount,
    };
  }, [userSubscriptions]);

  // Fonction pour copier dans le presse-papier
  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papier");
  };

  // Fonction pour rendre les cartes sur mobile - OPTIMISÉE
  const renderSubscriptionCard = (subscription) => (
    <motion.div
      key={subscription.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-gray-200 mb-4 last:mb-0 overflow-hidden"
    >
      <div className="p-4 border-b border-gray-100 bg-gray-50">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 border ${getStatusColor(
                  subscription.status,
                )} font-nunito font-semibold text-xs whitespace-nowrap`}
              >
                {getStatusIcon(subscription.status)}
                {getStatusLabel(subscription.status)}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 border bg-blue-50 text-blue-700 border-blue-200 font-nunito font-semibold text-xs whitespace-nowrap">
                <BiPackage className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate max-w-[80px]">Abonnement</span>
              </span>
            </div>
            <p className="font-nunito text-xs text-gray-500 whitespace-nowrap">
              {formatDate(subscription.created_at)}
            </p>
          </div>
          <div className="sm:text-right">
            <p className="font-poppins font-bold text-lg text-gray-900 break-words">
              {formatAmount(subscription.amount)} FCFA
            </p>
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Code - Optimisé pour mobile */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <BiCreditCard className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="font-nunito font-semibold text-xs text-gray-600">
              Code de l'abonnement
            </span>
          </div>
          <div className="flex items-center gap-2 bg-gray-50 p-2 border border-gray-200">
            <span className="font-nunito text-sm text-gray-700 break-all flex-1 min-w-0">
              {subscription.code}
            </span>
            <button
              onClick={() => copyToClipboard(subscription.code)}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              title="Copier le code"
            >
              <BiCopy className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Informations du plan - Empilé sur mobile */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <BiPackage className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span className="font-nunito font-semibold text-xs text-blue-700">
              Plan d'abonnement
            </span>
          </div>
          <div className="bg-blue-50 border border-blue-100 p-3">
            <p className="font-poppins font-semibold text-sm text-gray-900 break-words mb-1">
              {subscription.plan?.name || "N/A"}
            </p>
            {subscription.plan?.description && (
              <p className="font-nunito text-xs text-gray-600 break-words line-clamp-2">
                {subscription.plan.description}
              </p>
            )}
          </div>
        </div>

        {/* Informations client - Optimisé pour mobile */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <BiUser className="w-4 h-4 text-indigo-600 flex-shrink-0" />
            <span className="font-nunito font-semibold text-xs text-indigo-700">
              Informations client
            </span>
          </div>
          <div className="bg-indigo-50 border border-indigo-100 p-3 space-y-2">
            <div className="flex flex-col">
              <span className="font-nunito text-xs text-gray-500">Nom</span>
              <span className="font-nunito text-sm text-gray-700 break-words">
                {subscription.is_anonymous
                  ? subscription.anonymous_data?.name || "Anonyme"
                  : `${subscription.client.nom} ${subscription.client.prenom}`}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-2">
              <div className="flex items-start gap-2">
                <BiEnvelope className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="font-nunito text-xs text-gray-500">
                    Email
                  </span>
                  <p className="font-nunito text-sm text-gray-700 break-all">
                    {subscription.is_anonymous
                      ? subscription.anonymous_data?.email || "N/A"
                      : subscription.client.email}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <BiPhone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="font-nunito text-xs text-gray-500">
                    Téléphone
                  </span>
                  <p className="font-nunito text-sm text-gray-700 break-all">
                    {subscription.is_anonymous
                      ? subscription.anonymous_data?.telephone || "N/A"
                      : subscription.client.telephone}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Période et services - Deux colonnes sur mobile */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 bg-gray-50 border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              <BiCalendarEvent className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="font-nunito text-xs text-gray-600">Début</span>
            </div>
            <p className="font-nunito text-sm font-semibold text-gray-800 truncate">
              {formatDate(subscription.created_at)}
            </p>
          </div>
          <div className="p-3 bg-gray-50 border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              <BiCalendarCheck className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="font-nunito text-xs text-gray-600">Fin</span>
            </div>
            <p className="font-nunito text-sm font-semibold text-gray-800 truncate">
              {formatDate(subscription.end_at)}
            </p>
          </div>
        </div>

        {/* Services et bouton d'action */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <BiListUl className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <span className="font-nunito text-sm text-gray-700 block">
                {subscription.services?.length || 0} service
                {(subscription.services?.length || 0) !== 1 ? "s" : ""}
              </span>
              {subscription.services && subscription.services.length > 0 && (
                <span className="font-nunito text-xs text-gray-500">
                  {subscription.services.filter((s) => !s.used).length}{" "}
                  disponible
                  {subscription.services.filter((s) => !s.used).length !== 1
                    ? "s"
                    : ""}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => handleShowDetails(subscription)}
            className="px-4 py-2 bg-primary text-white hover:bg-primary/90 font-nunito font-semibold text-sm transition-colors whitespace-nowrap w-full sm:w-auto"
          >
            Voir les détails
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary to-secondary-100 py-4 px-2 sm:py-6 sm:px-4">
      <div className="w-full bg-white max-w-6xl p-3 sm:p-4 md:p-6 mx-auto">
        {/* En-tête avec titre et actions */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex-1 min-w-0">
              <h1 className="font-poppins font-bold text-xl sm:text-2xl text-gray-900 break-words">
                Mes Abonnements
              </h1>
              <p className="font-nunito text-sm text-gray-600 mt-1 break-words">
                Historique complet de tous mes abonnements
              </p>
            </div>
          </div>

          {/* Cartes de statistiques - Adapté pour mobile */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="bg-white border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-nunito text-xs text-gray-500 truncate">
                    Total Abonnements
                  </p>
                  <p className="font-poppins font-bold text-lg sm:text-xl text-gray-900 mt-1 truncate">
                    {stats.totalSubscriptions}
                  </p>
                </div>
                <div className="p-2 sm:p-3 bg-primary/10 flex-shrink-0 ml-2">
                  <BiPackage className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-nunito text-xs text-gray-500 truncate">
                    Montant Total
                  </p>
                  <p className="font-poppins font-bold text-lg sm:text-xl text-gray-900 mt-1 truncate">
                    {formatAmount(stats.totalAmount)} FCFA
                  </p>
                </div>
                <div className="p-2 sm:p-3 bg-emerald-100 flex-shrink-0 ml-2">
                  <BiDollar className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-nunito text-xs text-gray-500 truncate">
                    Abonnements Actifs
                  </p>
                  <p className="font-poppins font-bold text-lg sm:text-xl text-gray-900 mt-1 truncate">
                    {stats.activeSubscriptions}
                  </p>
                </div>
                <div className="p-2 sm:p-3 bg-emerald-100 flex-shrink-0 ml-2">
                  <BiCheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-nunito text-xs text-gray-500 truncate">
                    En attente
                  </p>
                  <p className="font-poppins font-bold text-lg sm:text-xl text-gray-900 mt-1 truncate">
                    {stats.pendingSubscriptions}
                  </p>
                </div>
                <div className="p-2 sm:p-3 bg-amber-100 flex-shrink-0 ml-2">
                  <BiTime className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section de filtres */}
        <div className="bg-white border border-gray-200 mb-6">
          <div className="p-3 sm:p-4 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <BiFilter className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <h2 className="font-poppins font-semibold text-lg text-gray-900 break-words">
                Filtres et Recherche
              </h2>
            </div>

            {/* Barre de recherche */}
            <div className="mb-4">
              <div className="relative">
                <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par code, nom du plan ou client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito text-sm"
                />
              </div>
            </div>

            {/* Filtres avancés */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Filtre période */}
              {/* <div>
                <label className="block font-nunito font-semibold text-xs text-gray-700 mb-2">
                  Période
                </label>
                <select
                  value={periodFilter}
                  onChange={(e) => handlePeriodChange(e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito text-sm"
                >
                  <option value="all">Toutes les périodes</option>
                  <option value="today">Aujourd'hui</option>
                  <option value="week">Cette semaine</option>
                  <option value="month">Ce mois</option>
                  <option value="custom">Période personnalisée</option>
                </select>
              </div> */}

              {/* Dates personnalisées */}
              {/* {periodFilter === "custom" && (
                <>
                  <div>
                    <label className="block font-nunito font-semibold text-xs text-gray-700 mb-2">
                      Date de début
                    </label>
                    <div className="flex items-center gap-2 border border-gray-300 px-3 py-2">
                      <BiCalendar className="text-gray-400 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full focus:outline-none font-nunito text-sm min-w-0"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-nunito font-semibold text-xs text-gray-700 mb-2">
                      Date de fin
                    </label>
                    <div className="flex items-center gap-2 border border-gray-300 px-3 py-2">
                      <BiCalendar className="text-gray-400 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full focus:outline-none font-nunito text-sm min-w-0"
                      />
                    </div>
                  </div>
                </>
              )} */}

              {/* Filtre statut */}
              <div>
                <label className="block font-nunito font-semibold text-xs text-gray-700 mb-2">
                  Statut
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito text-sm"
                >
                  <option value="">Tous les statuts</option>
                  <option value="ACTIVE">Actif</option>
                  <option value="PENDING">En attente</option>
                  <option value="EXPIRED">Expiré</option>
                  <option value="CANCELLED">Annulé</option>
                </select>
              </div>
            </div>

            {/* Résumé des filtres actifs */}
            {(periodFilter !== "all" || searchTerm || statusFilter) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-nunito text-xs text-gray-600 whitespace-nowrap">
                    Filtres actifs :
                  </span>
                  {periodFilter !== "all" && (
                    <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 font-nunito font-semibold text-xs whitespace-nowrap">
                      {getPeriodLabel()}
                    </span>
                  )}
                  {searchTerm && (
                    <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-1 font-nunito font-semibold text-xs break-words max-w-[150px]">
                      Recherche : "{truncateText(searchTerm, 20)}"
                    </span>
                  )}
                  {statusFilter && (
                    <span className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 px-2 py-1 font-nunito font-semibold text-xs whitespace-nowrap">
                      Statut : {getStatusLabel(statusFilter)}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Actions de filtre */}
          <div className="p-3 sm:p-4 bg-gray-50 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="font-nunito text-sm text-gray-600 whitespace-nowrap">
              <span className="font-semibold">
                {filteredSubscriptions.length}
              </span>{" "}
              abonnement{filteredSubscriptions.length !== 1 ? "s" : ""} trouvé
              {filteredSubscriptions.length !== 1 ? "s" : ""}
            </div>
            <div className="flex gap-2">
              {(periodFilter !== "all" || searchTerm || statusFilter) && (
                <button
                  onClick={resetFilters}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-nunito font-semibold text-sm transition-colors w-full sm:w-auto"
                >
                  <BiReset className="w-4 h-4" />
                  <span>Réinitialiser</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Liste des abonnements - Version responsive */}
        <div className="bg-white border border-gray-200 mb-6 overflow-hidden">
          {/* En-tête du tableau/des cartes */}
          <div className="p-3 sm:p-4 border-b border-gray-200">
            <h2 className="font-poppins font-semibold text-lg text-gray-900 break-words">
              Liste des Abonnements
              {periodFilter !== "all" && (
                <span className="text-gray-600 font-normal">
                  {" "}
                  - {getPeriodLabel()}
                </span>
              )}
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
              <p className="font-nunito text-sm text-gray-600 mt-2">
                Chargement des abonnements...
              </p>
            </div>
          ) : paginatedSubscriptions.length === 0 ? (
            <div className="p-8 text-center">
              <BiPackage className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="font-nunito text-base text-gray-600">
                Aucun abonnement trouvé
              </p>
              <p className="font-nunito text-sm text-gray-500 mt-1">
                Essayez de modifier vos filtres de recherche
              </p>
            </div>
          ) : (
            <>
              {/* Version desktop - Tableau */}
              <div className="hidden lg:block">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900 min-w-[120px]">
                          Code
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900 min-w-[150px] max-w-[200px]">
                          Plan
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900 min-w-[100px]">
                          Montant
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900 min-w-[100px]">
                          Statut
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900 min-w-[90px]">
                          Début
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900 min-w-[90px]">
                          Fin
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900 min-w-[100px]">
                          Services
                        </th>
                        <th className="text-left p-4 font-poppins font-semibold text-sm text-gray-900 min-w-[100px]">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedSubscriptions.map((subscription) => (
                        <motion.tr
                          key={subscription.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-2 max-w-[120px]">
                              <span className="font-nunito font-semibold text-sm text-gray-700 truncate">
                                {subscription.code}
                              </span>
                              <button
                                onClick={() =>
                                  copyToClipboard(subscription.code)
                                }
                                className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                title="Copier"
                              >
                                <BiCopy className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                          <td className="p-4 max-w-[200px]">
                            <div className="font-nunito text-sm text-gray-700">
                              <p
                                className="font-semibold truncate"
                                title={subscription.plan?.name}
                              >
                                {subscription.plan?.name || "N/A"}
                              </p>
                              {subscription.plan?.description && (
                                <p
                                  className="text-xs text-gray-600 mt-1 line-clamp-2"
                                  title={subscription.plan.description}
                                >
                                  {subscription.plan.description}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="font-poppins font-bold text-sm sm:text-base text-gray-900 whitespace-nowrap">
                              {formatAmount(subscription.amount)} FCFA
                            </div>
                          </td>
                          <td className="p-4">
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 border ${getStatusColor(
                                subscription.status,
                              )} font-nunito font-semibold text-xs whitespace-nowrap`}
                            >
                              {getStatusIcon(subscription.status)}
                              {getStatusLabel(subscription.status)}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="font-nunito text-sm text-gray-700 whitespace-nowrap">
                              {formatDate(subscription.created_at)}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="font-nunito text-sm text-gray-700 whitespace-nowrap">
                              {formatDate(subscription.end_at)}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span className="font-nunito text-sm text-gray-700 whitespace-nowrap">
                                {subscription.services?.length || 0} service
                                {(subscription.services?.length || 0) !== 1
                                  ? "s"
                                  : ""}
                              </span>
                              {(subscription.services?.length || 0) > 0 && (
                                <button
                                  onClick={() =>
                                    handleShowDetails(subscription)
                                  }
                                  className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary hover:bg-primary/20 font-nunito font-semibold text-xs transition-colors whitespace-nowrap"
                                >
                                  <BiListUl className="w-3 h-3" />
                                  Voir
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <button
                              onClick={() => handleShowDetails(subscription)}
                              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-nunito font-semibold text-xs transition-colors whitespace-nowrap"
                            >
                              Détails
                            </button>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Version mobile/tablette - Cartes */}
              <div className="lg:hidden">
                <div className="p-3 sm:p-4">
                  {paginatedSubscriptions.map(renderSubscriptionCard)}
                </div>
              </div>

              {/* Pagination */}
              <div className="p-3 sm:p-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="font-nunito text-sm text-gray-600 whitespace-nowrap">
                      Afficher
                    </span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito text-sm min-w-[60px]"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="font-nunito text-sm text-gray-600 whitespace-nowrap">
                      éléments
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex-shrink-0"
                    >
                      <BiChevronsLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex-shrink-0"
                    >
                      <BiChevronLeft className="w-4 h-4" />
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-8 h-8 flex items-center justify-center font-nunito text-sm flex-shrink-0 ${
                                currentPage === pageNum
                                  ? "bg-primary text-white border border-primary"
                                  : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        },
                      )}
                    </div>

                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex-shrink-0"
                    >
                      <BiChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex-shrink-0"
                    >
                      <BiChevronsRight className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="font-nunito text-sm text-gray-600 text-center sm:text-left whitespace-nowrap">
                    Page {currentPage} sur {totalPages} •{" "}
                    {filteredSubscriptions.length} résultat
                    {filteredSubscriptions.length !== 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal des détails - OPTIMISÉ pour mobile */}
      <AnimatePresence>
        {showDetailsModal && selectedSubscription && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={handleCloseModal}
            />

            {/* Modal Container */}
            <div className="flex min-h-screen items-center justify-center p-2 sm:p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white w-full max-w-3xl max-h-[90vh] flex flex-col"
              >
                {/* Header fixe */}
                <div className="bg-gradient-to-r from-primary to-pink-600 p-4 sm:p-6 flex-shrink-0">
                  <div className="flex items-start justify-between mb-4 gap-3">
                    <div className="flex-1 min-w-0">
                      <h2 className="font-poppins font-bold text-lg sm:text-xl text-white break-words">
                        Détails de l'Abonnement
                      </h2>
                      <p className="font-nunito text-xs sm:text-sm text-white/90 mt-1 break-words">
                        {formatDateTime(selectedSubscription.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={handleCloseModal}
                      className="text-white/80 hover:text-white transition-colors p-1 flex-shrink-0"
                    >
                      <BiX className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  </div>

                  {/* Informations résumé - Adapté pour mobile */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                    <div className="min-w-0">
                      <p className="font-nunito text-xs text-white/80 truncate">
                        Code
                      </p>
                      <div className="flex items-center gap-1 mt-1">
                        <p
                          className="font-poppins font-semibold text-xs sm:text-sm text-white truncate"
                          title={selectedSubscription.code}
                        >
                          {selectedSubscription.code}
                        </p>
                        <button
                          onClick={() =>
                            copyToClipboard(selectedSubscription.code)
                          }
                          className="text-white/80 hover:text-white flex-shrink-0"
                          title="Copier"
                        >
                          <BiCopy className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="font-nunito text-xs text-white/80 truncate">
                        Statut
                      </p>
                      <p
                        className={`inline-flex items-center gap-1 px-2 py-1 mt-1 ${getStatusColor(
                          selectedSubscription.status,
                        )} text-xs font-semibold truncate max-w-full`}
                        title={getStatusLabel(selectedSubscription.status)}
                      >
                        {getStatusIcon(selectedSubscription.status)}
                        <span className="truncate">
                          {getStatusLabel(selectedSubscription.status)}
                        </span>
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="font-nunito text-xs text-white/80 truncate">
                        Montant
                      </p>
                      <p className="font-poppins font-bold text-sm sm:text-base text-white truncate">
                        {formatAmount(selectedSubscription.amount)} FCFA
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="font-nunito text-xs text-white/80 truncate">
                        Client
                      </p>
                      <p
                        className="font-poppins font-semibold text-xs sm:text-sm text-white truncate"
                        title={
                          selectedSubscription.is_anonymous
                            ? selectedSubscription.anonymous_data?.name ||
                              "Anonyme"
                            : `${selectedSubscription.client.nom} ${selectedSubscription.client.prenom}`
                        }
                      >
                        {selectedSubscription.is_anonymous
                          ? selectedSubscription.anonymous_data?.name ||
                            "Anonyme"
                          : `${selectedSubscription.client.nom} ${selectedSubscription.client.prenom}`}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contenu scrollable */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                  {/* Informations du plan */}
                  <div className="mb-6">
                    <h3 className="font-poppins font-semibold text-base sm:text-lg text-gray-900 mb-3 break-words">
                      Plan d'abonnement
                    </h3>
                    <div className="bg-gradient-to-r from-blue-50 to-white p-3 sm:p-4 border border-blue-200">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 bg-blue-100 flex-shrink-0">
                          <BiPackage className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-poppins font-semibold text-sm sm:text-base text-gray-900 break-words mb-1">
                            {selectedSubscription.plan?.name || "N/A"}
                          </p>
                          {selectedSubscription.plan?.description && (
                            <p className="font-nunito text-xs sm:text-sm text-gray-600 mt-1 break-words">
                              {selectedSubscription.plan.description}
                            </p>
                          )}
                          <p className="font-nunito text-xs text-gray-500 mt-2 break-all">
                            Code: {selectedSubscription.plan?.code || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Période de validité */}
                  <div className="mb-6">
                    <h3 className="font-poppins font-semibold text-base sm:text-lg text-gray-900 mb-3 break-words">
                      Période de validité
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                      <div className="p-3 sm:p-4 bg-gray-50 border border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <BiCalendarEvent className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                          <span className="font-nunito font-semibold text-xs sm:text-sm text-gray-700 break-words">
                            Date de début
                          </span>
                        </div>
                        <p className="font-poppins font-bold text-base sm:text-lg text-gray-900 break-words">
                          {formatDate(selectedSubscription.created_at)}
                        </p>
                      </div>
                      <div className="p-3 sm:p-4 bg-gray-50 border border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <BiCalendarCheck className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                          <span className="font-nunito font-semibold text-xs sm:text-sm text-gray-700 break-words">
                            Date de fin
                          </span>
                        </div>
                        <p className="font-poppins font-bold text-base sm:text-lg text-gray-900 break-words">
                          {formatDate(selectedSubscription.end_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Liste des services */}
                  <div>
                    <h3 className="font-poppins font-semibold text-base sm:text-lg text-gray-900 mb-3 sm:mb-4 break-words">
                      Services ({selectedSubscription.services?.length || 0})
                    </h3>

                    {!selectedSubscription.services?.length ? (
                      <div className="text-center py-6 sm:py-8 bg-gray-50 border border-gray-200">
                        <BiListUl className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                        <p className="font-nunito text-sm sm:text-base text-gray-600">
                          Aucun service trouvé
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-3 sm:space-y-4 mb-6">
                          {selectedSubscription.services.map(
                            (service, index) => (
                              <div
                                key={service.id || index}
                                className="border border-gray-200 p-3 sm:p-4 hover:bg-gray-50 transition-colors"
                              >
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-poppins font-semibold text-sm sm:text-base text-gray-900 break-words">
                                            {service.service?.nom ||
                                              `Service ${index + 1}`}
                                          </span>
                                          <span
                                            className={`inline-flex items-center gap-1 px-2 py-1 border ${getServiceUsedColor(
                                              service.used,
                                            )} font-nunito font-semibold text-xs whitespace-nowrap flex-shrink-0`}
                                          >
                                            {getServiceUsedIcon(service.used)}
                                            {service.used
                                              ? "Utilisé"
                                              : "Disponible"}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-2 mt-2">
                                          <span
                                            className="font-nunito text-xs sm:text-sm text-gray-500 break-all flex-1 min-w-0"
                                            title={service.code}
                                          >
                                            {service.code}
                                          </span>
                                          <button
                                            onClick={() =>
                                              copyToClipboard(service.code)
                                            }
                                            className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                                            title="Copier le code"
                                          >
                                            <BiCopy className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </div>
                                      <div className="sm:text-right sm:min-w-[120px]">
                                        <div className="font-poppins font-bold text-base sm:text-lg text-gray-900 whitespace-nowrap">
                                          {formatAmount(
                                            service.service?.price || 0,
                                          )}{" "}
                                          FCFA
                                        </div>
                                        <div className="font-nunito text-xs text-gray-500 whitespace-nowrap">
                                          Prix unitaire
                                        </div>
                                      </div>
                                    </div>
                                    {service.service?.description && (
                                      <p className="font-nunito text-xs sm:text-sm text-gray-600 mt-2 break-words">
                                        {service.service.description}
                                      </p>
                                    )}
                                  </div>
                                </div>

                                {service.service?.durationMinutes && (
                                  <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                                    <BiTime className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    <span className="font-nunito text-xs sm:text-sm text-gray-700 break-words">
                                      Durée: {service.service.durationMinutes}{" "}
                                      minutes
                                    </span>
                                  </div>
                                )}
                              </div>
                            ),
                          )}
                        </div>

                        {/* Résumé des services */}
                        <div className="bg-gradient-to-r from-primary/5 to-pink-500/5 border border-primary/20 p-3 sm:p-4">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-poppins font-semibold text-sm sm:text-base text-gray-900 break-words">
                                Résumé des services
                              </p>
                              <div className="flex flex-wrap gap-2 sm:gap-4 mt-1">
                                <div className="font-nunito text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                                  Total: {selectedSubscription.services.length}
                                </div>
                                <div className="font-nunito text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                                  Utilisés:{" "}
                                  {
                                    selectedSubscription.services.filter(
                                      (s) => s.used,
                                    ).length
                                  }
                                </div>
                                <div className="font-nunito text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                                  Disponibles:{" "}
                                  {
                                    selectedSubscription.services.filter(
                                      (s) => !s.used,
                                    ).length
                                  }
                                </div>
                              </div>
                            </div>
                            <div className="text-right sm:min-w-[180px]">
                              <p className="font-poppins font-bold text-lg sm:text-2xl text-gray-900 break-words">
                                {formatAmount(selectedSubscription.amount)} FCFA
                              </p>
                              <p className="font-nunito text-xs sm:text-sm text-gray-600 break-words">
                                Montant total de l'abonnement
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Footer fixe */}
                <div className="border-t border-gray-200 p-4 sm:p-6 bg-white flex-shrink-0">
                  <div className="flex justify-end">
                    <button
                      onClick={handleCloseModal}
                      className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-nunito font-semibold text-sm transition-colors w-full sm:w-auto"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
